/**
 * Magazine features and press coverage.
 *
 * The actual copy on the MagazinePage comes from the `magazine` namespace in
 * src/i18n/*.json so it can be translated. This file holds the stable,
 * language-independent metadata (issue number, publication date, PDF URL,
 * external reader link, etc.).
 *
 * An issue may be one of:
 *   - "pdf"          — has its own PDF (rendered via the in-page PdfViewer)
 *   - "external"     — only links out to the publisher's site (HK01, etc.)
 *   - "image"        — newspaper clipping / scan; detail page at /magazine/:slug
 *                      with zoomable preview + link to the publisher
 *   - "article-link" — direct link to a specific news article; no publication /
 *                      issue / interviewee / summary framing. Same card layout
 *                      as `external` (CTA opens external_url in a new tab).
 *
 * Add a new issue by appending another `MagazineIssue` to `MAGAZINES`. The
 * most recent issue (by `date`) is what the homepage MagazineFeature shows.
 */

export type MagazineIssue =
  | PdfMagazineIssue
  | ExternalMagazineIssue
  | ImageMagazineIssue
  | ArticleLinkMagazineIssue

interface BaseMagazineIssue {
  /** URL-safe identifier, e.g. "education-professional-49-202609" */
  slug: string
  /** Magazine / publication name in the local language, e.g. 《教育專業》 */
  publication: string
  /** Publication name in English, e.g. Education Professional */
  publicationEn: string
  /** Issue label in the local language, e.g. 第 49 期 — leave empty if N/A */
  issue: string
  /** Issue label in English, e.g. Issue 49 — leave empty if N/A */
  issueEn: string
  /** Publication date as an ISO 8601 string (YYYY-MM-DD) */
  date: string
  /** Subject of the interview / article */
  interviewee: string
  /** Featured title of the piece */
  title: string
  /** Short 1–2 sentence Chinese summary shown under the hero */
  summary: string
  /** Optional cover poster image path, served from public/ — must start with "/" */
  coverImage?: string
  /**
   * The `cms_media.uuid` of the cover, when the cover comes from the media
   * library.
   *
   * The card needs it to serve the cover from the responsive ladder: the
   * cover is drawn in a 288px frame but used to load the 1024px variant, and
   * a phone paid for all of it. A STATIC cover (`/media/…`) has no uuid and
   * keeps its plain <img>.
   */
  coverMediaUuid?: string
  /** Native width of `coverImage` in pixels — used to reserve aspect-ratio space and prevent CLS */
  coverWidth?: number
  /** Native height of `coverImage` in pixels */
  coverHeight?: number
}

export interface PdfMagazineIssue extends BaseMagazineIssue {
  kind: 'pdf'
  /** Path to the PDF, served from public/ — must start with "/" */
  pdfPath: string
  /** Optional external reader (e.g. goodschool.hk flipbook) shown as a secondary CTA */
  externalUrl?: string
}

export interface ExternalMagazineIssue extends BaseMagazineIssue {
  kind: 'external'
  /** External article URL — the primary CTA on the card opens this in a new tab */
  externalUrl: string
}

export interface ImageMagazineIssue extends BaseMagazineIssue {
  kind: 'image'
  /** Full clipping / scan — required; shown on the detail page with zoom */
  coverImage: string
  coverWidth: number
  coverHeight: number
  /** Publisher article URL — secondary CTA on the detail page */
  externalUrl: string
}

/**
 * Direct link to a specific news article — no publication / issue /
 * interviewee / summary framing. The card's primary CTA opens
 * `externalUrl` in a new tab, same as `external`. Rendered on the
 * `/magazine` list only; no `/magazine/:slug` detail page.
 */
export interface ArticleLinkMagazineIssue extends BaseMagazineIssue {
  kind: 'article-link'
  /** Specific article URL — the only required content field */
  externalUrl: string
}

/** All magazine / press issues, ordered as displayed on the site. */
export const MAGAZINES: MagazineIssue[] = [
  {
    kind: 'pdf',
    slug: 'education-professional-49-202609',
    publication: '《教育專業》',
    publicationEn: 'Education Professional',
    issue: '第 49 期',
    issueEn: 'Issue 49',
    date: '2026-09-11',
    interviewee: '魏遠強先生',
    title: '應用 AI 推動生涯規劃創新發展',
    summary:
      '青年耀進發展協會行政總監魏遠強先生指出，全球四成技能正面臨重新裝備。生涯規劃服務需要從「回應式問答」走向「提問驅動的前瞻探索」，以人機協同、雙軌輔導、微認證四大方向協助香港青年應對 AI 時代的就業變局。',
    pdfPath: '/media/education-professional-no49-yada-202609.pdf',
    coverImage: '/media/education-professional-no49-yada-202609-cover.png',
    coverWidth: 619,
    coverHeight: 800,
    externalUrl: 'https://www.goodschool.hk/ebook/reader/20260911',
  },
  {
    kind: 'external',
    slug: 'hk01-parent-lecture-20260915',
    publication: '《香港01》',
    publicationEn: 'HK01',
    issue: '家長講座',
    issueEn: 'Parent Lecture',
    date: '2026-09-15',
    interviewee: '魏遠強先生',
    title: '《香港01》家長講座　青年耀進發展協會行政總監分享AI時代教養法',
    summary:
      '魏遠強先生應邀主講「親子同行 x 健康使用AI」家長講座，深入剖析學童使用 AI 的認知卸載與「討好症」盲點，提出「家長應做副駕駛」的核心心法，並推介「家庭AI合約」與「思考三明治」學習法，協助家長在科技世代陪伴孩子前行。',
    coverImage: '/media/hk01-parent-lecture-20260915-cover.jpeg',
    coverWidth: 1200,
    coverHeight: 675,
    externalUrl:
      'https://www.hk01.com/%E4%B8%AD%E5%B0%8F%E5%AD%B8%E6%A0%A1%E5%9C%88/60388011/%E9%A6%99%E6%B8%AF01-%E5%AE%B6%E9%95%B7%E8%AC%9B%E5%BA%A7-%E9%9D%92%E5%B9%B4%E8%80%80%E9%80%B2%E7%99%BC%E5%B1%95%E5%8D%94%E6%9C%83%E8%A1%8C%E6%94%BF%E7%B8%BD%E7%9B%A3%E5%88%86%E4%BA%ABai%E6%99%82%E4%BB%A3%E6%95%99%E9%A4%8A%E6%B3%95',
  },
  {
    kind: 'image',
    slug: 'mingpao-digital-philanthropy-part2-20260310',
    publication: '《明報》',
    publicationEn: 'Ming Pao',
    issue: '經濟版 B6',
    issueEn: 'Economy B6',
    date: '2026-03-10',
    interviewee: '魏遠強先生',
    title: '數字公益進化之路（二之二）',
    summary:
      '魏遠強先生於《明報》「ICB 明報商機」專欄續談數字公益：從直播公益與平台生態，到 AI 預測式公益、物聯網微公益，以至社區韌性數位樞紐，勾勒公益由效率、體驗走向前瞻的進化路徑。',
    coverImage: '/media/mingpao-digital-philanthropy-part2-20260310.png',
    coverWidth: 712,
    coverHeight: 1430,
    externalUrl:
      'https://news.mingpao.com/pns/%E7%B6%93%E6%BF%9F/article/20260310/s00004/1773074205782/%E9%AD%8F%E9%81%A0%E5%BC%B7-%E6%95%B8%E5%AD%97%E5%85%AC%E7%9B%8A%E9%80%B2%E5%8C%96%E4%B9%8B%E8%B7%AF%EF%BC%88%E4%BA%8C%E4%B9%8B%E4%BA%8C%EF%BC%89',
  },
  {
    kind: 'image',
    slug: 'mingpao-digital-philanthropy-part1-20260224',
    publication: '《明報》',
    publicationEn: 'Ming Pao',
    issue: '經濟版 B6',
    issueEn: 'Economy B6',
    date: '2026-02-24',
    interviewee: '魏遠強先生',
    title: '數字公益進化之路（二之一）',
    summary:
      '魏遠強先生於《明報》「ICB 明報商機」專欄剖析數字公益的演進：從紙本到網站、社交媒體動員關係資本，以至透明度由年報轉向實時數據流，梳理科技如何重塑公益運作。',
    coverImage: '/media/mingpao-digital-philanthropy-part1-20260224.png',
    coverWidth: 610,
    coverHeight: 1414,
    externalUrl:
      'https://finance.mingpao.com/fin/daily/20260224/1771868655568/%E9%AD%8F%E9%81%A0%E5%BC%B7-%E6%95%B8%E5%AD%97%E5%85%AC%E7%9B%8A%E9%80%B2%E5%8C%96%E4%B9%8B%E8%B7%AF%EF%BC%88%E4%BA%8C%E4%B9%8B%E4%B8%80%EF%BC%89',
  },
]

/** Most recent issue — what the homepage MagazineFeature highlights. */
export const FEATURED_MAGAZINE: MagazineIssue | undefined = MAGAZINES[0]

/** Find an issue by slug — used by the /magazine page detail route. */
export function findMagazineBySlug(slug: string): MagazineIssue | undefined {
  return MAGAZINES.find((m) => m.slug === slug)
}